/* eslint-disable max-lines */
import { ChartThemeColors, ValueMapChartPoints } from '@customTypes';

import { HUNDRED, ZERO } from './common';

export const chartsLoadingConfig = {
  default: {
    color: '#c23531',
    textColor: '#000',
    maskColor: 'rgba(255, 255, 255, 0.8)',
    zlevel: 0,
    fontSize: 12,
    showSpinner: true,
    spinnerRadius: 10,
    lineWidth: 5,
    fontWeight: 'normal',
    fontStyle: 'normal',
    fontFamily: 'Montserrat, Arial',
  },
};

const relativeQualityKey = 'project.analysis.charts.valueMap.';

export const RELATIVE_QUALITY_OPTIONS = [
  { i18nKey: `${relativeQualityKey}relativeQuality`, key: 'CVaProj.VaGrid.21', value: 'relativePerceivedQualityChart' },
  { i18nKey: `${relativeQualityKey}quality`, key: 'CVaProj.VaGrid.4', value: 'relativeProductQualityChart' },
  { i18nKey: `${relativeQualityKey}service`, key: 'CVaProj.VaGrid.5', value: 'relativeServiceQualityChart' },
  { i18nKey: `${relativeQualityKey}image`, key: 'CVaProj.VaGrid.6', value: 'relativeImageQualityChart' },
];

export const MIN_BARS_COUNT = 5;

export const ATTRIBUTE_BAR_HEIGHT = 25;
export const RELATIVE_BAR_HEIGHT = 30;

export const ALLOWED_TICKS = [0, 1, 2, 5, 10, 15, 20, 30, 45, 50, 100, 150, 200];

export enum CHART_SETTINGS {
  VALUE_MAP,
  ATTRIBUTE,
}

export enum BUBBLE_SIZE {
  POINT,
  INTERVIEW_NUMBER,
  MARKET_SHARE,
}

export const BUBBLE_SIZE_OPTIONS = [
  {
    i18nKey: 'project.analysis.chartSettings.points',
    key: 'CvaProj.Setup138',
    value: BUBBLE_SIZE.POINT,
  },
  {
    i18nKey: 'project.analysis.chartSettings.mentionedInterviews',
    key: 'CvaProj.Setup139',
    value: BUBBLE_SIZE.INTERVIEW_NUMBER,
  },
  {
    i18nKey: 'project.analysis.chartSettings.marketShare',
    key: 'CvaProj.Setup140',
    value: BUBBLE_SIZE.MARKET_SHARE,
  },
];

export const valueMapSettingsDefaultValues = {
  xrange: null,
  yrange: null,
  bubbleSize: BUBBLE_SIZE.POINT,
  hideNames: false,
  intervalX: ZERO,
  intervalY: ZERO,
};

export const attributeSettingsDefaultValues = {
  maxRating: ZERO,
  minRating: ZERO,
  minImportance: ZERO,
  maxImportance: ZERO,
  hideNames: false,
};

export const MAX_BUBBLE_SIZE = 20;
export enum TIME_SERIES_BAR_INDEX {
  RATING_DIFFERENCE,
  ATTRIBUTE,
  IMPORTANCE,
}

export enum TIME_SERIES_LINE_INDEX {
  COMPETITORS,
  PRICE,
  VALUE,
}

export const TIME_SERIES_BAR_COLORS = ['#FF7043', '#0057A7', '#FFB507', '#26A69A', '#B82C90', '#03A9F4'];

export const BUBBLE_SIZE_FILTER_LENGTH = 22;

export const COLUMNS_TO_HIDE = [
  'supplierId',
  'shortName',
  'marketShare',
  'rangeStart',
  'rangeEnd',
  'thisBusinessValue',
  'seq',
];

export enum CHART_EXPORT_TYPE {
  PNG = 'png',
  SVG = 'svg',
}

export const CHART_TYPE_OPTIONS = [
  {
    label: 'PNG',
    value: CHART_EXPORT_TYPE.PNG,
  },
  {
    label: 'SVG',
    value: CHART_EXPORT_TYPE.SVG,
  },
];

export enum CHART_WIDTH_PRESET {
  FULL = 1124,
  TWO_THIRDS = 740,
  HALF = 548,
  ONE_THIRD = 368,
  QUARTER = 276,
}

export enum CHART_HEIGHT_PRESET {
  FULL = 650,
  TWO_THIRDS = 436,
  HALF = 324,
  ONE_THIRD = 212,
}

export const CHART_WIDTH_OPTIONS = [
  {
    i18nkey: 'project.analysis.outputOptions.full',
    key: 'CvaProj.Setup156',
    value: CHART_WIDTH_PRESET.FULL,
  },
  {
    i18nkey: 'project.analysis.outputOptions.twoThirds',
    key: 'CvaProj.Setup157',
    value: CHART_WIDTH_PRESET.TWO_THIRDS,
  },
  {
    i18nkey: 'project.analysis.outputOptions.half',
    key: 'CvaProj.Setup158',
    value: CHART_WIDTH_PRESET.HALF,
  },
  {
    i18nkey: 'project.analysis.outputOptions.oneThird',
    key: 'CvaProj.Setup159',
    value: CHART_WIDTH_PRESET.ONE_THIRD,
  },
  {
    i18nkey: 'project.analysis.outputOptions.quarter',
    key: 'CvaProj.Setup160',
    value: CHART_WIDTH_PRESET.QUARTER,
  },
];

export const CHART_HEIGHT_OPTIONS = [
  {
    i18nkey: 'project.analysis.outputOptions.full',
    key: 'CvaProj.Setup156',
    value: CHART_HEIGHT_PRESET.FULL,
  },
  {
    i18nkey: 'project.analysis.outputOptions.twoThirds',
    key: 'CvaProj.Setup157',

    value: CHART_HEIGHT_PRESET.TWO_THIRDS,
  },
  {
    i18nkey: 'project.analysis.outputOptions.half',
    key: 'CvaProj.Setup158',
    value: CHART_HEIGHT_PRESET.HALF,
  },
  {
    i18nkey: 'project.analysis.outputOptions.oneThird',
    key: 'CvaProj.Setup159',
    value: CHART_HEIGHT_PRESET.ONE_THIRD,
  },
];

export const initialChartExportFormState = {
  type: CHART_EXPORT_TYPE.PNG,
  usePresetSizes: true,
  width: CHART_WIDTH_PRESET.FULL,
  height: CHART_HEIGHT_PRESET.FULL,
  percentWidth: HUNDRED,
  percentHeight: HUNDRED,
};

export const TBUS_ICON =
  'Cjxzdmcgd2lkdGg9IjI4IiBoZWlnaHQ9IjI4IiB2aWV3Qm94PSIwIDAgMjggMjgiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CiAgPGcgb3BhY2l0eT0iLjkiPgogICAgPGNpcmNsZSBjeD0iMTQiIGN5PSIxNCIgcj0iMTQiIGZpbGw9IiMyODk2RkMiIGZpbGwtb3BhY2l0eT0iLjMiLz4KICAgIDxjaXJjbGUgb3BhY2l0eT0iLjkiIGN4PSIxNCIgY3k9IjE0IiByPSI1IiBmaWxsPSIjMDAwRDFBIiBzdHJva2U9IiNmZmYiLz4KICA8L2c+Cjwvc3ZnPgo=';
export const TBUS_ICON_DARK =
  'Cjxzdmcgd2lkdGg9IjI4IiBoZWlnaHQ9IjI4IiB2aWV3Qm94PSIwIDAgMjggMjgiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CiAgPGcgb3BhY2l0eT0iLjkiPgogICAgPHBhdGggZmlsbD0iIzgwQzFGRiIgZmlsbC1vcGFjaXR5PSIuMyIgZD0iTTAgMTRhMTQgMTQgMCAxIDAgMjggMGExNCAxNCAwIDEgMCAtMjggMCIgLz4KICAgIDxwYXRoIG9wYWNpdHk9Ii45IiBmaWxsPSIjRkZGRkZGIiBzdHJva2U9IiNmZmYiIGQ9Ik05IDE0YTUgNSAwIDEgMCAxMCAwYTUgNSAwIDEgMCAtMTAgMCIgLz4KICA8L2c+Cjwvc3ZnPgo=';

export const defaultAttributeCompetitor = 'All';

export const AXIS_LABEL_ROTATE_45 = 45;
export const GRID_INCREASED_MARGIN = 100;

export const CHART_FULLSCREEN_CONFIG = {
  dataZoom: {
    type: 'inside',
  },
};

export const TOOLTIP_ITEM_COUNT = 4;

export const chartsTableKeysMap: Record<string, string> = {
  value: 'CvaProj.RelValHead',
  quality: 'CVaProj.VaGrid.3',
  price: 'CVaProj.VaGrid.2',
  importance: 'CvaInput.PriceSense6',
  performanceAttr: 'CvaInput.Summary33',
  importanceAttr: 'CvaInput.Wizz2',
  relPerformance: 'CvaProj.RelPerf',
  mentionsCount: 'Cvaproj.Setup73',
  name: 'CvaInput.SegPage3',
  supplierName: 'CvaInput.Wizz0',
  ratingDifferences: 'CvaProj.Setup98',
};

export const similarPointsConfig = {
  accessors: {
    price: (p: ValueMapChartPoints) => p.price,
    quality: (p: ValueMapChartPoints) => p.quality,
  },
  thresholds: {
    price: 2.5,
    quality: 2.5,
  },
};

export const SCATTER_CHART_LABEL_MAX_LENGHT = 20;

export const LIGHT_THEME_COLORS: ChartThemeColors = {
  label: '#000D1AB2',
  line25: '#000D1A40',
  line50: '#000D1A80',
  line100: '#0F172A',
  item: '#000D1A',
  transition: '#f4f6f800',
  baseLine: '#0079E8',
  background: '#FFFFFF',
  timeSeriesLines: ['#0057A7', '#82CD19', '#03A9F4', '#B82C90', '#FFB229', '#FF7043'],
};

export const DARK_THEME_COLORS: ChartThemeColors = {
  label: '#FFFFFFBF',
  line25: '#FFFFFF40',
  line50: '#FFFFFF80',
  line100: '#E4E9ED',
  item: '#FFFFFF',
  transition: '#000D1A00',
  baseLine: '#80C1FF',
  background: '#000D1A',
  timeSeriesLines: ['#1E90FF', '#ADFF2F', '#00CED1', '#FF1493', '#FFD700', '#FF4500'],
};
